import * as React from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    ListHeader,
    HeaderActionButton,
    SkeletonLoader,
    ListSkeleton,
    EmptyBox,
    HeaderListSkeleton,
} from '@europrocurement/flexy-components';
import {
    ColumnDatatable,
    FiltersDatatableList,
    StoreDatatable,
} from '@europrocurement/flexy-datatable';
import {
    Prescripteur,
    PrescripteursSelector,
    useTiersService,
    TiersAppDispatch,
} from '@europrocurement/l2d-domain';
import { useInitialLoadingState } from '@europrocurement/l2d-hooks';

import { createPrescriberIcon, FaOptionIcon } from '@europrocurement/l2d-icons';
import { faBuildings, faPerson } from '@fortawesome/pro-duotone-svg-icons';
import { Box } from '@mui/material';
import { useAppSelector } from '../../../../redux/types';
import { customizerSelector } from '../../../../redux/RootStore';
import { PrescripteursListingActions } from './PrescripteursListingActions';
import { PRESCRIBER_NEW } from '../../constants/paths';

const PrescripteursListing: React.FunctionComponent = function () {
    const dispatch = useDispatch<TiersAppDispatch>();
    const navigate = useNavigate();

    const datasource = useAppSelector(PrescripteursSelector).main;
    const { xIdSociete } = useAppSelector(customizerSelector);
    const { getPrescripteurs } = useTiersService();

    const { isInitialLoading, isInError } = useInitialLoadingState(datasource);

    // Chargement initial des données
    React.useEffect(() => {
        dispatch(getPrescripteurs({}));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const columns: ColumnDatatable<Prescripteur>[] = React.useMemo(
        () => [
            {
                label: 'Statut',
                displayLabel: false,
                render: ({ personnePhysique, demo }) =>
                    personnePhysique ? (
                        <FaOptionIcon
                            icon={faPerson}
                            color={demo ? 'error' : 'primary'}
                        />
                    ) : (
                        <FaOptionIcon
                            icon={faBuildings}
                            color={demo ? 'error' : 'primary'}
                        />
                    ),
                isDisplayed: true,
                sortable: true,
                field: 'raisonSociale',
            },
            {
                label: 'Code Client',
                render: ({ relationsSocietes }) =>
                    relationsSocietes.find((item) => item.societe?.id === xIdSociete)?.codeClient ||
                    '',
                isDisplayed: true,
            },
            {
                label: 'Raison sociale',
                render: ({ raisonSociale }) => raisonSociale,
                isDisplayed: true,
                sortable: true,
                field: 'raisonSociale',
            },
            {
                label: 'CP Ville',
                render: ({ adresses }) => {
                    if (!adresses || !adresses.length) return null;
                    const { ville, codePostal } = adresses[0];
                    return `${codePostal} ${ville.toUpperCase()}`;
                },
                isDisplayed: true,
            },
            {
                label: 'Siret',
                render: 'siret',
                isDisplayed: false,
            },
            {
                label: 'Contact',
                render: ({ contacts }: Prescripteur) => {
                    if (!contacts || !contacts.length) return null;
                    return `${contacts[0].nomContact.toUpperCase()} ${contacts[0].prenomContact}`;
                },
                isDisplayed: true,
            },
            {
                label: 'Actions',
                displayLabel: false,
                render: (item) => item.id && <PrescripteursListingActions itemId={item.id} />,
                isDisplayed: true,
            },
        ],
        [xIdSociete],
    );

    const filters: FiltersDatatableList = React.useMemo(
        () => [
            { label: 'Demo', field: 'demo', type: 'boolean' },
            { label: 'supprimé', field: 'deleted', type: 'boolean' },
            { label: 'Code client', field: 'relationsSocietes.codeClient', type: 'text' },
            { label: 'Raison sociale', field: 'raisonSociale', type: 'text' },
            { label: 'Siret', field: 'siret', type: 'text' },
            { label: 'Siren', field: 'siren', type: 'text' },
            { label: 'Email contacts', field: 'contacts.emailsContact.email', type: 'text' },
            {
                label: 'Telephone contacts',
                field: 'contacts.telephonesContact.telephone',
                type: 'text',
            },
            { label: 'Nom contact', field: 'contacts.nomContact', type: 'text' },
            { label: 'Prénom contact', field: 'contacts.prenomContact', type: 'text' },
            { label: 'Nom Commercial', field: 'commerciaux.utilisateur.nom', type: 'text' },
            { label: 'Prénom Commercial', field: 'commerciaux.utilisateur.prenom', type: 'text' },
            { label: 'Formaliste', field: 'formaliste.nom', type: 'text' },
            { label: 'adresse', field: 'adresses.codePostal', type: 'text' },
        ],
        [],
    );

    const errorContent = React.useMemo(
        () => (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '80vh',
                    width: '100%',
                }}
            >
                <EmptyBox
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'space-evenly',
                        height: '25%',
                        width: '50%',
                    }}
                    text="Une erreur s'est produite lors de la récupération des données, échec du chargement des prescripteurs."
                    subText="Veuillez recharger la page. Si le problème persiste, contactez votre administrateur."
                />
            </Box>
        ),
        [],
    );

    return (
        <SkeletonLoader
            isLoading={isInitialLoading}
            skeleton={
                <>
                    <HeaderListSkeleton />
                    <ListSkeleton
                        showSearch
                        columns={6}
                    />
                </>
            }
            isInError={isInError}
            errorChildren={errorContent}
        >
            <ListHeader
                breadcrumbPath={[
                    { path: 'Prescripteurs', link: '/prescripteurs/liste' },
                    "Recherche d'un prescripteur",
                    'Liste des prescripteurs',
                ]}
                actions={
                    <HeaderActionButton
                        label="Créer un prescripteur"
                        icon={createPrescriberIcon}
                        onClick={() => navigate(PRESCRIBER_NEW)}
                        actionType="navigation"
                    />
                }
            />

            {datasource && (
                <StoreDatatable
                    dataSource={datasource}
                    columns={columns}
                    fetchData={getPrescripteurs}
                    filters={filters}
                    localStorageKey="FlexyStoreDatatable"
                    onClickRow={(_, prescripteur) => navigate(`${prescripteur?.id}`)}
                    observers={[xIdSociete]}
                />
            )}
        </SkeletonLoader>
    );
};

export default PrescripteursListing;
